import { isDemoTenant } from '@lib';
import { isTokenExpired } from '@utils';
import { useMemo } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';
import { appRoutes, AppRoute } from './router.config';

declare const abp: any;

/**
 * Walks the route tree and returns every entry that has a `path`. Grouping
 * entries (Invoices, Inventory, Settings) carry only `children`, so they are
 * skipped while their descendants are kept.
 */
const flattenRoutes = (routes: AppRoute[]): AppRoute[] =>
  routes.flatMap((route) => [
    ...(route.path ? [route] : []),
    ...(route.children ? flattenRoutes(route.children) : []),
  ]);

/**
 * Resolved on first render rather than at module scope: router.config imports this file
 * while this file imports appRoutes from it, so reading appRoutes during module
 * initialisation hits the temporal dead zone. By render time both modules are ready.
 */
let flatAppRoutes: AppRoute[] | null = null;

const getFlatAppRoutes = () => {
  flatAppRoutes ??= flattenRoutes(appRoutes);
  return flatAppRoutes;
};

export const RequireAuth = () => {
  const location = useLocation();
  const tokenExpired = isTokenExpired();

  const route = useMemo(
    () => getFlatAppRoutes().find((r: AppRoute) => matchPath(r.path as string, location.pathname)),
    [location.pathname]
  );

  if (tokenExpired || !abp.session.userId) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!abp.session.tenantId && location.pathname === '/') {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  if (route && route.permission && !abp.auth.isGranted(route.permission)) {
    return (
      <Navigate
        to={abp.session.tenantId ? '/calendar' : '/dashboard'}
        state={{ from: location }}
        replace
      />
    );
  }

  if (route?.paidOnly && isDemoTenant()) {
    return <Navigate to="/calendar" state={{ from: location }} replace />;
  }

  return <Outlet />;
};
